import React, { useState, useEffect, useRef } from 'react';
import { HiUsers } from 'react-icons/hi2';
import { IoChevronDown, IoShareSocial, IoShareSocialSharp } from 'react-icons/io5';
import { MdBookmark, MdBookmarkBorder } from 'react-icons/md';
import { BsEmojiSmile } from 'react-icons/bs';
import { FaTrophy } from 'react-icons/fa';
import { IniciativaFormatada } from 'types/iniciativa';
import { ModalIniciativa } from '../../Iniciativas/components/ModalIniciativa';
import { useAuth } from 'context/AuthContext';
import {
  registrarElogio,
  registrarFavorito,
  registrarCompartilhamento
} from 'services/iniciativa-action.service';
import './IniciativaCard.scss';

type IniciativaCardProps = IniciativaFormatada;

export const IniciativaCard: React.FC<IniciativaCardProps> = (props) => {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isComplimentOpen, setIsComplimentOpen] = useState(false);
  const [selectedCompliment, setSelectedCompliment] = useState<string>(props.usuarioElogio || '');
  const complimentRef = useRef<HTMLDivElement>(null);
  
  // Estados para controle de ações
  const [isFavorited, setIsFavorited] = useState(props.usuarioFavoritou);
  const [isShared, setIsShared] = useState(props.usuarioCompartilhou);
  const [loadingAction, setLoadingAction] = useState(false);

  const complimentOptions = ['Arrasou!', 'Iniciativa inspiradora!', 'Superou expectativas!'];

  // Fecha o dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (complimentRef.current && !complimentRef.current.contains(event.target as Node)) {
        setIsComplimentOpen(false);
      }
    };

    if (isComplimentOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isComplimentOpen]);

  const handleComplimentSelect = async (compliment: string) => {
    if (!user?._id || loadingAction) return;

    try {
      setLoadingAction(true);
      setSelectedCompliment(compliment);
      setIsComplimentOpen(false);

      await registrarElogio(user._id, {
        id_iniciativa: props.id,
        elogio: compliment
      });
    } catch (error) {
      setSelectedCompliment('');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!user?._id || loadingAction) return;

    try {
      setLoadingAction(true);
      const novoStatus = !isFavorited;

      await registrarFavorito(user._id, {
        id_iniciativa: props.id,
        ativo: novoStatus
      });

      setIsFavorited(novoStatus);
    } catch (error) {
      // Erro silencioso
    } finally {
      setLoadingAction(false);
    }
  };

  const handleShare = async () => {
    if (!user?._id || loadingAction) return;

    try {
      setLoadingAction(true);

      await registrarCompartilhamento(user._id, {
        id_iniciativa: props.id
      });

      setIsShared(true);

      // Usa a API Web Share se disponível
      if (navigator.share) {
        await navigator.share({
          title: props.titulo,
          text: props.descricao,
          url: window.location.href
        });
      } else {
        // Fallback: copia o link para a área de transferência
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copiado para a área de transferência!');
      }
    } catch (error) {
      // Erro silencioso
    } finally {
      setLoadingAction(false);
    }
  };

  const {
    tema,
    titulo,
    descricao,
    autores,
    categoria,
    corCategoria,
    imagem,
    videoUrl,
    originalVideoUrl,
    classificadaEtapa1,
    classificadaEtapa2
  } = props;

  const showClassificadaEtapa2 = Boolean(classificadaEtapa2);
  const showClassificadaEtapa1 = Boolean(classificadaEtapa1) && !showClassificadaEtapa2;
  const showClassificada = showClassificadaEtapa1 || showClassificadaEtapa2;
  const cardClassName = [
    'iniciativa-card',
    showClassificada ? 'iniciativa-card--classificada' : '',
    showClassificadaEtapa2 ? 'iniciativa-card--classificada-etapa2' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const tagElement = (
    <div
      className={`iniciativa-card__tag tag-${corCategoria}`}
      title={categoria}
    >
      {categoria}
    </div>
  );

  const mediaElement = videoUrl ? (
    <a
      href={originalVideoUrl || videoUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="iniciativa-card__video-link"
    >
      <img
        src={imagem}
        alt="Clique para assistir ao vídeo da iniciativa"
        className="iniciativa-card__image"
      />
    </a>
  ) : (
    <img
      src={imagem}
      alt={`Imagem da iniciativa ${titulo}`}
      className="iniciativa-card__image"
    />
  );

  return (
    <>
      <div className={cardClassName} role="article" aria-label={`Iniciativa: ${titulo}`}>
        {showClassificada ? (
          <div className="iniciativa-card__media-frame">
            <div className="iniciativa-card__classificada-header">
              <span className="iniciativa-card__classificada-label">
                <FaTrophy size={20} aria-hidden="true" />
                {showClassificadaEtapa2 ? 'CLASSIFICADA ETAPA 2' : 'CLASSIFICADA ETAPA 1'}
              </span>
              {tagElement}
            </div>
            <div className="iniciativa-card__image-container">{mediaElement}</div>
          </div>
        ) : (
          <div className="iniciativa-card__image-container">
            {mediaElement}
            {tagElement}
          </div>
        )}

        <div className="iniciativa-card__content">
          <h3 className="iniciativa-card__category">{tema}</h3>
          <h4 className="iniciativa-card__title">{titulo}</h4>
          <p className="iniciativa-card__text">{descricao}</p>

          <div className="iniciativa-card__author">
            <HiUsers size={16} />
            <span>{autores}</span>
          </div>

          <div className="iniciativa-card__action-bar">
            <div className="iniciativa-card__action-left">
              <div className="iniciativa-card__icon-wrapper">
                <BsEmojiSmile size={20} style={{ color: selectedCompliment ? '#5D5FEF' : '#666666' }} />
                {props.totalElogios > 0 && (
                  <span className="iniciativa-card__badge">{props.totalElogios}</span>
                )}
              </div>
              <div className="iniciativa-card__compliment-container" ref={complimentRef}>
                <div
                  className="iniciativa-card__compliment-dropdown"
                  onClick={() => !loadingAction && setIsComplimentOpen(!isComplimentOpen)}
                  style={{
                    opacity: loadingAction ? 0.6 : 1,
                    cursor: loadingAction ? 'not-allowed' : 'pointer',
                    borderColor: selectedCompliment ? '#5D5FEF' : '#E0E7FF',
                    color: selectedCompliment ? '#5D5FEF' : '#666666'
                  }}
                >
                  <span>{selectedCompliment || 'Faça um elogio!'}</span>
                  <IoChevronDown size={16} />
                </div>
                {isComplimentOpen && !loadingAction && (
                  <div className="iniciativa-card__compliment-menu">
                    {complimentOptions.map((compliment) => (
                      <button
                        key={compliment}
                        className="iniciativa-card__compliment-option"
                        onClick={() => handleComplimentSelect(compliment)}
                      >
                        {compliment}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="iniciativa-card__action-right">
              <div className="iniciativa-card__icon-wrapper">
                <button
                  className="iniciativa-card__icon-btn"
                  title="Compartilhar"
                  onClick={handleShare}
                  disabled={loadingAction}
                  style={{
                    opacity: loadingAction ? 0.6 : 1,
                    color: isShared ? '#5D5FEF' : '#666666'
                  }}
                >
                  {isShared ? <IoShareSocialSharp size={20} /> : <IoShareSocial size={20} />}
                </button>
                {props.totalCompartilhamentos > 0 && (
                  <span className="iniciativa-card__badge">{props.totalCompartilhamentos}</span>
                )}
              </div>
              <div className="iniciativa-card__icon-wrapper">
                <button
                  className="iniciativa-card__icon-btn"
                  title={isFavorited ? "Remover dos favoritos" : "Salvar nos favoritos"}
                  onClick={handleToggleFavorite}
                  disabled={loadingAction}
                  style={{
                    opacity: loadingAction ? 0.6 : 1,
                    color: isFavorited ? '#5D5FEF' : '#666666'
                  }}
                >
                  {isFavorited ? <MdBookmark size={20} /> : <MdBookmarkBorder size={20} />}
                </button>
                {props.totalFavoritos > 0 && (
                  <span className="iniciativa-card__badge">{props.totalFavoritos}</span>
                )}
              </div>
            </div>
          </div>

          <button
            className="iniciativa-card__button"
            onClick={() => setIsModalOpen(true)}
          >
            VER MAIS DETALHES
          </button>
        </div>
      </div>

    <ModalIniciativa 
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      iniciativa={props}
    />
    </>
  );
};
